import * as http from "http"

import Axios, {
    AxiosError,
    AxiosRequestConfig,
    AxiosResponse,
    isAxiosError,
    AxiosHeaderValue,
} from "axios"
import axiosRetry, { exponentialDelay } from "axios-retry"
import { saveAs } from "file-saver"

import { IS_PRODUCTION, IS_STAGING, REST_API_URL } from "../env-management"
import { SentryWrapper } from "../ThirdParty/Sentry/SentryWrapper"
import { ErrorResponse } from "../interfaces"

export { type AxiosError, isAxiosError }

const DEFAULT_MAX_ATTEMPTS = IS_PRODUCTION ? 4 : 1

const responseBody = <T>(response: AxiosResponse<T>) => response.data

export const axiosClient = Axios.create({
    baseURL: REST_API_URL,
    withCredentials: true,
    httpAgent: new http.Agent({ keepAlive: true }),
})

function getFileNameFromHeader(str: AxiosHeaderValue): Record<string, string> | undefined {
    if (!str || typeof str !== "string") return undefined
    return Object.fromEntries(
        str
            .replace(/"/g, "")
            .split("; ")
            .map((str) => str.trim().split("="))
    ) as Record<string, string>
}

export const httpClient = {
    get: <T>(url: string, config?: AxiosRequestConfig) =>
        axiosClient
            .get<T>(url, config)
            .then(responseBody)
            .catch((e: AxiosError) => {
                throw e
            }),
    post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
        axiosClient
            .post<T>(url, data, config)
            .then(responseBody)
            .catch((e: AxiosError) => {
                throw e
            }),
    put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
        axiosClient
            .put<T>(url, data, config)
            .then(responseBody)
            .catch((e: AxiosError) => {
                throw e
            }),
    delete: <T>(url: string, config?: AxiosRequestConfig) =>
        axiosClient
            .delete<T>(url, config)
            .then(responseBody)
            .catch((e: AxiosError) => {
                throw e
            }),
}

export function toAxiosError<T>(e: unknown) {
    return isAxiosError(e) ? (e as AxiosError<Partial<T>>) : undefined
}

axiosRetry(axiosClient, {
    retries: DEFAULT_MAX_ATTEMPTS,
    retryDelay: (...arg) => exponentialDelay(...arg, 3000),
    retryCondition: (error) => {
        // for e2e test purpose only
        if (IS_STAGING && error.config?.url === "psychologist/events/possible-hours") return false

        const status = error.response?.status
        const fatal = status && status >= 400 && status < 500
        return !fatal
    },
})

axiosClient.interceptors.response.use(
    function (response) {
        if (response.data instanceof Blob || typeof response.data === "string") {
            const contentDisposition = getFileNameFromHeader(
                response.headers["content-disposition"] as AxiosHeaderValue
            )
            if (contentDisposition) {
                saveAs(response.data, contentDisposition.filename)
            }
        }

        return response
    },
    function (error: Error | AxiosError) {
        if (isAxiosError(error)) {
            const response = error.response as AxiosResponse & ErrorResponse
            const config = error.config as AxiosRequestConfig
            if (response.status >= 500) {
                const cleanUrl = config.url ? config.url.replace(/\?.*$/, "?...") : undefined
                SentryWrapper.getInstance().captureException(
                    `${cleanUrl} - Unexpected ${config.method} error.`,
                    {
                        extra: { url: cleanUrl, data: response.data, status: response.status },
                    }
                )
            }
        }
        return Promise.reject(error)
    }
)

export const httpClientKeepAlive = Axios.create({
    baseURL: REST_API_URL,
    timeout: 1000,
    headers: {
        "Content-Type": "text/plain; charset=utf-8",
    },
    httpAgent: new http.Agent({ keepAlive: true }),
})
